import isEmpty from 'lodash/isEmpty';
import { useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getPosSyncedOffers } from 'shared/helpers/specials';
import { FilteredSpecialsDocument } from 'src/gql/graphql';
import type { Specials, Products } from 'src/gql/graphql';

export type ProductOffersInput = Pick<Products, 'DispensaryID' | 'POSPricingData' | 'specialData'>;

const MENU_PRICING_ROLLOUT_FLAG = 'core.cats-ecomm.dpos-menu-pricing.rollout';
const POS_SYNCED_PRICING_VARIANTS = [2, 3];

export default function useProductOffers(product: ProductOffersInput): { specials: Specials[]; loading: boolean } {
  const flags = useFlags();
  const isPosSyncedPricingActive = POS_SYNCED_PRICING_VARIANTS.includes(Number(flags[MENU_PRICING_ROLLOUT_FLAG]));
  const hasLegacyOffers = !isEmpty(product.specialData?.bogoSpecials);

  const { data, loading } = useQuery(FilteredSpecialsDocument, {
    skip: !hasLegacyOffers,
    variables: {
      specialsFilter: {
        dispensaryId: product.DispensaryID,
        current: true,
        specialIds: (product.specialData?.bogoSpecials ?? [])
          .map((s) => s?.specialId)
          .filter((id): id is string => id != null),
      },
    },
  });

  if (!hasLegacyOffers && isPosSyncedPricingActive) {
    // No legacy bogoSpecials to fetch via FilteredSpecials -- if this product has POS-synced
    // condition/reward discounts instead, build lightweight Specials-shaped stand-ins directly
    // from POSPricingData rather than a second round-trip. There's no Specials Mongo document
    // backing these ids, but the offer card components only ever read _id/menuDisplayName/name/
    // specialType off a special, so this is sufficient to render and link them.
    const posSyncedOffers = getPosSyncedOffers(product).map(
      ({ id, menuDisplayName }) =>
        (({
          _id: id,
          specialId: id,
          menuDisplayName,
          name: menuDisplayName,
          specialType: 'bogo',
        } as unknown) as Specials)
    );

    return { specials: posSyncedOffers, loading: false };
  }

  const specials = (data?.filteredSpecials?.specials ?? []).filter((s): s is Specials => s != null);

  return { specials, loading };
}
